import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import {
  getShipmentInfoIntro,
  getShipmentInfoLines,
  getShipmentLink,
  SHIPMENT_INFO_SIGN_OFF,
  SHIPMENT_LINK_LABEL,
} from "./utils";

// Evotrux3 modal: title left, cancel first, one filled button. The font stack
// is explicit because dialogs render in a portal outside the page container.
const FONT = "'Quicksand', 'Helvetica Neue', Arial, sans-serif";

const BUTTON_SX = {
  textTransform: "none",
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "4px",
  minHeight: 36,
  boxShadow: "none",
} as const;

interface Props {
  open: boolean;
  onClose: VoidFunction;
  onCopy: VoidFunction;
  shipment: CondensedShipment;
}

export function TellMoreCarriersModal(props: Props) {
  const { open, onClose, onCopy, shipment } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 4px 16px rgba(13,46,69,0.14)",
          fontFamily: FONT,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 18,
          fontWeight: 600,
          fontFamily: FONT,
          color: "#212121",
          padding: "16px 24px 8px",
        }}
      >
        Tell More Carriers
      </DialogTitle>
      <DialogContent>
        <Typography
          sx={{ fontSize: 14, color: "#212121", fontFamily: FONT, mb: 2 }}
        >
          Copy the shipment info below and paste it into an email or text to
          share your shipment with &amp; get quotes from more carriers.
        </Typography>
        <Box
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#F5F5F5",
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            fontFamily: FONT,
            fontSize: 14,
            color: "#212121",
            wordBreak: "break-word",
          }}
        >
          {/* Shows what gets copied, line titles bold as they paste into email. */}
          <Box sx={{ mb: 2 }}>{getShipmentInfoIntro(shipment)}</Box>
          {getShipmentInfoLines(shipment).map(({ label, value }) => (
            <div key={label}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {label}:
              </Box>{" "}
              {value}
            </div>
          ))}
          <Box sx={{ mt: 2 }}>
            {SHIPMENT_LINK_LABEL} {getShipmentLink(shipment)}
          </Box>
          <Box sx={{ mt: 2 }}>{SHIPMENT_INFO_SIGN_OFF}</Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "8px 24px 16px" }}>
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            ...BUTTON_SX,
            color: "#1976D2",
            px: 1,
            "&:hover": { backgroundColor: "#F5F5F5" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<ContentCopyIcon />}
          onClick={onCopy}
          sx={{
            ...BUTTON_SX,
            px: 2,
            backgroundColor: "#1976D2",
            "&:hover": { backgroundColor: "#1565C0", boxShadow: "none" },
          }}
        >
          Copy shipment info
        </Button>
      </DialogActions>
    </Dialog>
  );
}
